// src/pages/Home.jsx

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiEdit,
  FiEye,
  FiFileText,
  FiShield,
  FiTrendingUp,
  FiDollarSign,
  FiLock,
  FiAlertCircle,
  FiArrowRight,
  FiChevronDown,
  FiCheckCircle,
  FiZap,
  FiGrid,
} from "react-icons/fi";

import InstallPrompt from "../components/InstallPrompt";
import { AuthContext } from "../context/AuthContext";


const ensureHomeResponsiveCSS = (() => {
  const id = "home-responsive-style-v8-marketing";
  if (typeof document === "undefined") return;
  if (document.getElementById(id)) return;

  const style = document.createElement("style");
  style.id = id;
  style.innerHTML = `
    html, body, #root { background: #FFFFFF !important; }
    body { margin: 0 !important; }

    body.home-route .main-content { overflow-y: auto !important; }

    @media (min-width: 1025px) {
      body.home-route .main-content {
        scrollbar-gutter: stable;
        scrollbar-width: auto;
        scrollbar-color: rgba(15, 23, 42, 0.35) transparent;
      }
      body.home-route .main-content::-webkit-scrollbar { width: 12px; }
      body.home-route .main-content::-webkit-scrollbar-track { background: transparent; }
      body.home-route .main-content::-webkit-scrollbar-thumb {
        background: rgba(15, 23, 42, 0.30);
        border-radius: 999px;
        border: 3px solid transparent;
        background-clip: content-box;
      }
      body.home-route .main-content::-webkit-scrollbar-thumb:hover {
        background: rgba(15, 23, 42, 0.42);
        border: 3px solid transparent;
        background-clip: content-box;
      }
    }

    .home-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 18px 38px rgba(2, 6, 23, 0.12) !important;
    }

    .home-card:focus-visible,
    .home-nav-btn:focus-visible,
    .home-cta-btn:focus-visible,
    .home-open-btn:focus-visible {
      outline: 3px solid rgba(37, 99, 235, 0.25);
      outline-offset: 3px;
    }

    .home-mobile-hero-preview {
      display: none;
    }

    .home-mobile-hero-stack {
      display: none;
    }

    @media (max-width: 1024px) {
      .home-wrap { padding-top: 0px !important; }
      .home-hero-shell {
        min-height: auto !important;
        background: #FFFFFF !important;
      }
      .home-hero-content {
        max-width: 720px !important;
        padding: 28px 24px !important;
      }
      .home-hero-image {
        display: none !important;
      }
      .home-hero-wash {
        display: none !important;
      }
      .home-mobile-hero-preview {
        display: block !important;
        position: relative !important;
        height: 210px !important;
        margin: 18px 0 2px !important;
        border-radius: 18px !important;
        border: 1px solid rgba(15, 23, 42, 0.08) !important;
        box-shadow: 0 14px 30px rgba(2, 6, 23, 0.10) !important;
        background-size: cover !important;
        background-position: center right !important;
        background-repeat: no-repeat !important;
        overflow: hidden !important;
      }
      .home-mobile-hero-stack {
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
        gap: 10px !important;
        position: absolute !important;
        top: 50% !important;
        left: 24px !important;
        transform: translateY(-50%) !important;
        z-index: 2 !important;
      }
      .home-meta-row {
        display: none !important;
      }
      .home-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
      .home-card-bullets {
        font-size: 15px !important;
        line-height: 1.62 !important;
      }
      .home-read-more-btn {
        font-size: 15px !important;
        min-height: 42px !important;
      }
      .home-open-btn {
        font-size: 14.5px !important;
        min-height: 42px !important;
      }
      .home-card-subtitle {
        font-size: 15px !important;
        line-height: 1.48 !important;
      }
      .home-trust-strip {
        grid-template-columns: 1fr !important;
        align-items: stretch !important;
      }
      .home-trust-strip > div {
        justify-content: flex-start !important;
        text-align: left !important;
        width: 100% !important;
      }
    }

    @media (max-width: 640px) {
      .home-wrap { padding: 14px 12px 22px !important; }
      .home-hero-content { padding: 24px 18px !important; }
      .home-hero-title { font-size: 29px !important; line-height: 1.12 !important; }
      .home-hero-subtitle { font-size: 15px !important; line-height: 1.55 !important; }
      .home-mobile-hero-preview {
        height: 178px !important;
        margin-top: 16px !important;
        border-radius: 16px !important;
        background-position: 64% center !important;
      }
      .home-mobile-hero-stack {
        left: 16px !important;
        gap: 8px !important;
      }
      .home-cta-row { flex-direction: column !important; align-items: stretch !important; }
      .home-cta-row button { width: 100% !important; justify-content: center !important; }
      .home-benefit-line { align-items: flex-start !important; }
      .home-meta-row { gap: 7px !important; }
      .home-grid { grid-template-columns: 1fr !important; }
      .home-grid-header { justify-content: center !important; text-align: center !important; }
      .home-grid-title { text-align: center !important; width: 100% !important; font-size: 24px !important; }
      .home-card-top { align-items: flex-start !important; }
      .home-card-subtitle { white-space: normal !important; }
      .home-trust-strip { grid-template-columns: 1fr !important; }
      .home-footer-inner { justify-content: center !important; text-align: center !important; }
    }
  `;
  document.head.appendChild(style);
})();

export default function Home() {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [openKey, setOpenKey] = useState("vision");

  const isLoggedIn = Boolean(currentUser);

  useEffect(() => {
    try {
      document.body.classList.add("home-route");
      return () => document.body.classList.remove("home-route");
    } catch {
      return undefined;
    }
  }, []);


  // ✅ HERO IMAGE PATH:
  // Save/use the generated hero image here:
  // public/assets/WWW.ASKLAWLY.COM.png
  const HERO_BG = `${process.env.PUBLIC_URL}/assets/WWW.ASKLAWLY.COM.png`;

  const go = (href) => {
    if (!href) return;
    navigate(href);
    try {
      window.scrollTo(0, 0);
    } catch {}
  };

  const scrollToFeatures = () => {
    try {
      document.getElementById("core-features")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {}
  };

  const cards = useMemo(
    () => [
      {
        key: "chat",
        title: "Chat",
        subtitle: "Get instant answers to legal questions with AI.",
        icon: <FiEdit />,
        href: "/dashboard",
        bullets: [
          "Ask legal questions in plain English.",
          "Get clear, structured answers with next steps.",
          "Save chats so you can continue later.",
        ],
        accent: "chat",
      },
      {
        key: "vision",
        title: "Vision Assist",
        subtitle: "Upload an image or document and get step-by-step guidance.",
        icon: <FiEye />,
        href: "/vision-assist",
        bullets: [
          "Use guided help for forms, portals, documents, and screens.",
          "Get visual explanations you can act on.",
          "Helpful for detailed, real-world workflows.",
        ],
        tag: "Featured",
        accent: "vision",
      },
      {
        key: "contract",
        title: "Contract Insight",
        subtitle: "Analyze contracts, detect risks, and review key terms.",
        icon: <FiShield />,
        href: "/contract-insight",
        bullets: [
          "Review clauses, missing protections, and red flags.",
          "Get plain-English findings and key-term explanations.",
          "Designed for longer agreements and structured summaries.",
        ],
        accent: "contract",
      },
      {
        key: "docwriter",
        title: "DocWriter",
        subtitle: "Draft letters, agreements, notices, and legal documents.",
        icon: <FiFileText />,
        href: "/docwriter/start",
        bullets: [
          "Create polished drafts from guided prompts.",
          "Adjust tone, audience, language, and reading level.",
          "Save outputs and revise when needed.",
        ],
        accent: "docwriter",
      },
      {
        key: "filevault",
        title: "FileVault",
        subtitle: "Store, organize, and protect your documents securely.",
        icon: <FiLock />,
        href: "/file-vault",
        bullets: [
          "Keep important documents in one organized place.",
          "Use secure storage for case files and business records.",
          "Built around a privacy-first workflow.",
        ],
        accent: "filevault",
      },
      {
        key: "smartalert",
        title: "Smart Alert",
        subtitle: "Notify trusted contacts by SMS and email when it matters.",
        icon: <FiAlertCircle />,
        href: "/smart-alert",
        bullets: [
          "Configure trusted contacts and alert settings.",
          "Use practical reminders and escalation workflows.",
          "Helpful for important dates and safety check-ins.",
        ],
        accent: "smartalert",
      },
      {
        key: "estimator",
        title: "Case Estimator",
        subtitle: "Get directional cost and timeline guidance based on inputs.",
        icon: <FiTrendingUp />,
        href: "/cost-pilot",
        bullets: [
          "Estimate typical ranges before contacting a provider.",
          "Use fast inputs and practical guidance.",
          "Good for planning and expectation setting.",
        ],
        accent: "estimator",
      },
      {
        key: "casevalue",
        title: "Case Value",
        subtitle: "Compare directional case value scenarios.",
        icon: <FiDollarSign />,
        href: "/case-value",
        bullets: [
          "Understand possible value ranges and assumptions.",
          "Compare scenarios before deeper review.",
          "Works well alongside Contract Insight and DocWriter.",
        ],
        accent: "casevalue",
      },
      {
        key: "start",
        title: "Get Started",
        subtitle: isLoggedIn ? "Open your workspace and begin in seconds." : "Create your free workspace and start today.",
        icon: <FiArrowRight />,
        href: isLoggedIn ? "/dashboard" : "/signup",
        bullets: [
          isLoggedIn ? "Start a new chat session." : "Open a free account quickly.",
          "Use the right tool for the task.",
          "Keep your work organized in one workspace.",
        ],
        accent: "start",
      },
    ],
    [isLoggedIn]
  );


  return (
    <div style={styles.page}>
      <div style={styles.wrap} className="home-wrap">
        {/* HERO */}
        <section id="home-hero" style={styles.heroShell} className="home-hero-shell" aria-label="Hero">
          <div
            style={{
              ...styles.heroBg,
              backgroundImage: `url(${HERO_BG})`,
            }}
            className="home-hero-image"
            aria-hidden="true"
          />
          <div style={styles.heroWash} className="home-hero-wash" aria-hidden="true" />

          <div style={styles.heroContent} className="home-hero-content">
            <div style={styles.kicker}>{isLoggedIn ? "Your AskLawly Workspace" : "AskLawly"}</div>

            <h1 style={styles.h1} className="home-hero-title">
              {isLoggedIn
                ? "Welcome back to your legal workspace."
                : "A modern legal workspace for chat, documents, contracts, and visual help."}
            </h1>

            <p style={styles.h2} className="home-hero-subtitle">
              {isLoggedIn
                ? "Continue with chat, document drafting, contract review, secure file storage, or guided visual help — all from one place."
                : "Ask questions, draft documents, review contracts, and get guided help — all in one secure workspace."}
            </p>

            <div style={styles.ctaRow} className="home-cta-row">
              <button
                type="button"
                className="home-cta-btn"
                style={styles.primaryBtn}
                onClick={() => go(isLoggedIn ? "/dashboard" : "/signup")}
              >
                {isLoggedIn ? "Start New Chat" : "Open Free Account"} <FiArrowRight style={styles.inlineIcon} />
              </button>

              <button
                type="button"
                className="home-cta-btn"
                style={styles.secondaryBtn}
                onClick={() => (isLoggedIn ? go("/vision-assist") : scrollToFeatures())}
              >
                {isLoggedIn ? "Try Vision Assist" : "Explore Tools"} <FiArrowRight style={styles.inlineIcon} />
              </button>
            </div>

            <div
              className="home-mobile-hero-preview"
              style={{
                ...styles.mobileHeroPreview,
                backgroundImage: `url(${HERO_BG})`,
              }}
              aria-hidden="true"
            >
              <div className="home-mobile-hero-stack" style={styles.mobileHeroStack}>
                <div style={styles.mobileMetaPill}>
                  <FiZap style={styles.mobileMetaIcon} /> Fast
                </div>
                <div style={styles.mobileMetaPill}>
                  <FiLock style={styles.mobileMetaIcon} /> Secure
                </div>
                <div style={styles.mobileMetaPill}>
                  <FiGrid style={styles.mobileMetaIcon} /> Workflow-ready
                </div>
              </div>
            </div>

            <div style={styles.benefitLine} className="home-benefit-line">
              <FiShield style={styles.benefitIcon} />
              <span>
                {isLoggedIn
                  ? "Your tools are ready for chat, documents, contracts, and secure workflows."
                  : "Free account includes saved chats, access to core tools, and secure document workflows."}
              </span>
            </div>

            <div style={styles.heroMetaRow} className="home-meta-row">
              <div style={styles.metaPill}>
                <FiZap /> Fast
              </div>
              <div style={styles.metaPill}>
                <FiLock /> Secure
              </div>
              <div style={styles.metaPill}>
                <FiGrid /> Workflow-ready
              </div>
            </div>
          </div>
        </section>

        {/* FEATURE GRID */}
        <section id="core-features" style={styles.gridSection} aria-label="Core features">
          <div style={styles.gridHeader} className="home-grid-header">
            <div style={styles.sectionEyebrow}>Core Features</div>
            <h2 style={styles.gridTitle} className="home-grid-title">
              Everything you need, in one place.
            </h2>
          </div>

          <div style={styles.grid} className="home-grid">
            {cards.map((c) => {
              const isOpen = openKey === c.key;
              const accent = accentMap[c.accent] || accentMap.default;

              return (
                <article
                  key={c.key}
                  className="home-card"
                  style={{
                    ...styles.card,
                    borderColor: isOpen ? accent.border : "rgba(15, 23, 42, 0.08)",
                    boxShadow: isOpen ? "0 16px 34px rgba(2, 6, 23, 0.12)" : "0 10px 26px rgba(2, 6, 23, 0.075)",
                  }}
                >
                  <button
                    type="button"
                    style={styles.cardClickableArea}
                    onClick={() => setOpenKey(isOpen ? "" : c.key)}
                    aria-expanded={isOpen}
                    aria-label={`${isOpen ? "Hide" : "Read more about"} ${c.title}`}
                  >
                    <div style={{ ...styles.cardIcon, background: accent.iconBg, color: accent.color }}>{c.icon}</div>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={styles.cardTitleRow}>
                        <div style={styles.cardTitle}>{c.title}</div>
                        {c.tag ? <div style={styles.badge}>{c.tag}</div> : null}
                      </div>
                      <div style={styles.cardSubtitle} className="home-card-subtitle">
                        {c.subtitle}
                      </div>
                    </div>
                  </button>

                  <div style={styles.cardBody}>
                    <div
                      style={{
                        ...styles.accordion,
                        maxHeight: isOpen ? 220 : 0,
                        opacity: isOpen ? 1 : 0,
                        marginTop: isOpen ? 10 : 0,
                      }}
                    >
                      <ul style={styles.bullets} className="home-card-bullets">
                        {c.bullets.map((b, i) => (
                          <li key={i} style={styles.bulletItem}>
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div style={styles.cardBottomRow}>
                      <button
                        type="button"
                        className="home-open-btn"
                        style={styles.openBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          go(c.href);
                        }}
                      >
                        Open
                      </button>

                      <button
                        type="button"
                        className="home-read-more-btn"
                        style={styles.readMoreBtn}
                        onClick={() => setOpenKey(isOpen ? "" : c.key)}
                        aria-label={`${isOpen ? "Hide details for" : "Read more about"} ${c.title}`}
                      >
                        {isOpen ? "Read less" : "Read more"} <FiChevronDown style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.16s ease" }} />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section style={styles.trustStrip} className="home-trust-strip" aria-label="Trust and security">
          <div style={styles.trustItem}>
            <FiShield /> Trusted by legal professionals & businesses
          </div>
          <div style={styles.trustItem}>
            <FiLock /> Bank-level security
          </div>
          <div style={styles.trustItem}>
            <FiCheckCircle /> Privacy-first by design
          </div>
          <div style={styles.trustItem}>
            <FiFileText /> Your data stays yours
          </div>
        </section>

        {/* FOOTER */}
        <footer style={styles.footer}>
          <div style={styles.footerInner} className="home-footer-inner">
            <div style={styles.footerBrand}>AskLawly</div>

            <div style={styles.footerLinks}>
              <InstallPrompt
                label="Install App"
                buttonStyle={{
                  appearance: "none",
                  border: "1px solid rgba(15, 23, 42, 0.12)",
                  background: "#1E2A78",
                  cursor: "pointer",
                  padding: "8px 10px",
                  borderRadius: 12,
                  color: "#FFFFFF",
                  fontWeight: 800,
                  fontSize: 12,
                }}
                helperStyle={{
                  color: "#334155",
                }}
              />

              <button style={styles.footerLinkBtn} onClick={() => go("/about")} type="button">
                About
              </button>

              <button style={styles.footerLinkBtn} onClick={() => go("/contact")} type="button">
                Contact
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

const accentMap = {
  default: {
    color: "#1E2A78",
    iconBg: "rgba(30, 42, 120, 0.08)",
    border: "rgba(30, 42, 120, 0.24)",
  },
  chat: {
    color: "#2563EB",
    iconBg: "rgba(37, 99, 235, 0.10)",
    border: "rgba(37, 99, 235, 0.26)",
  },
  vision: {
    color: "#4F46E5",
    iconBg: "rgba(79, 70, 229, 0.10)",
    border: "rgba(79, 70, 229, 0.26)",
  },
  contract: {
    color: "#059669",
    iconBg: "rgba(5, 150, 105, 0.10)",
    border: "rgba(5, 150, 105, 0.24)",
  },
  docwriter: {
    color: "#0F766E",
    iconBg: "rgba(15, 118, 110, 0.10)",
    border: "rgba(15, 118, 110, 0.22)",
  },
  filevault: {
    color: "#1D4ED8",
    iconBg: "rgba(29, 78, 216, 0.10)",
    border: "rgba(29, 78, 216, 0.22)",
  },
  smartalert: {
    color: "#EA580C",
    iconBg: "rgba(234, 88, 12, 0.10)",
    border: "rgba(234, 88, 12, 0.22)",
  },
  estimator: {
    color: "#7C3AED",
    iconBg: "rgba(124, 58, 237, 0.10)",
    border: "rgba(124, 58, 237, 0.22)",
  },
  casevalue: {
    color: "#0EA5E9",
    iconBg: "rgba(14, 165, 233, 0.10)",
    border: "rgba(14, 165, 233, 0.22)",
  },
  start: {
    color: "#F59E0B",
    iconBg: "rgba(245, 158, 11, 0.13)",
    border: "rgba(245, 158, 11, 0.22)",
  },
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "#F5F7FA",
    position: "relative",
    overflowX: "hidden",
  },

  wrap: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "18px 16px 26px",
    position: "relative",
    background: "#F5F7FA",
  },

  publicHeader: {
    minHeight: 62,
    marginBottom: 14,
    padding: "10px 12px",
    borderRadius: 18,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 10px 26px rgba(2,6,23,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    boxSizing: "border-box",
  },
  brandBtn: {
    appearance: "none",
    border: "none",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    padding: 0,
    minWidth: 0,
  },
  logoFull: {
    width: 152,
    maxWidth: "42vw",
    height: "auto",
    objectFit: "contain",
    display: "block",
  },
  logoFallback: {
    display: "none",
    fontSize: 24,
    fontWeight: 950,
    color: "#0F172A",
    letterSpacing: "-0.5px",
  },
  publicNavLinks: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    flex: "1 1 auto",
  },
  navBtn: {
    appearance: "none",
    border: "none",
    background: "transparent",
    color: "#0F172A",
    fontWeight: 800,
    fontSize: 13,
    cursor: "pointer",
    padding: "9px 10px",
    borderRadius: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
  },
  navChevron: {
    fontSize: 14,
    strokeWidth: 2.2,
  },
  publicActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: "0 0 auto",
  },
  loginNavBtn: {
    appearance: "none",
    border: "1px solid rgba(15,23,42,0.14)",
    background: "rgba(255,255,255,0.9)",
    borderRadius: 12,
    padding: "11px 16px",
    color: "#0F172A",
    fontWeight: 900,
    cursor: "pointer",
  },
  signupNavBtn: {
    appearance: "none",
    border: "none",
    background: "linear-gradient(135deg, #1E2A78 0%, #2563EB 70%, #0B5FFF 120%)",
    borderRadius: 12,
    padding: "12px 16px",
    color: "#FFFFFF",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 12px 24px rgba(37,99,235,0.22)",
  },

  heroShell: {
    position: "relative",
    width: "100%",
    borderRadius: 20,
    overflow: "hidden",
    minHeight: 382,
    marginBottom: 24,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    boxShadow: "0 14px 36px rgba(2, 6, 23, 0.10)",
    background: "#FFFFFF",
    boxSizing: "border-box",
  },
  heroBg: {
    position: "absolute",
    inset: 0,
    backgroundSize: "cover",
    backgroundPosition: "center right",
    backgroundRepeat: "no-repeat",
    transform: "none",
    opacity: 1,
  },
  heroWash: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.94) 28%, rgba(255,255,255,0.62) 47%, rgba(255,255,255,0.14) 66%, rgba(255,255,255,0.00) 100%)",
    pointerEvents: "none",
  },
  mobileHeroPreview: {
    backgroundSize: "cover",
    backgroundPosition: "center right",
    backgroundRepeat: "no-repeat",
    position: "relative",
    overflow: "hidden",
  },
  mobileHeroStack: {
    position: "absolute",
    top: "50%",
    left: 22,
    transform: "translateY(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 10,
    zIndex: 2,
  },
  mobileMetaPill: {
    padding: "8px 12px",
    borderRadius: 999,
    background: "linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(245,248,255,0.94) 100%)",
    border: "1px solid rgba(37, 99, 235, 0.14)",
    color: "#334155",
    fontWeight: 850,
    fontSize: 12.5,
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    whiteSpace: "nowrap",
  },
  mobileMetaIcon: {
    color: "#1E2A78",
    fontSize: 13.5,
    strokeWidth: 2.4,
  },
  heroContent: {
    position: "relative",
    minHeight: 382,
    padding: "30px 28px 28px 32px",
    maxWidth: 570,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  kicker: {
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "flex-start",
    padding: "7px 12px",
    borderRadius: 999,
    background: "rgba(37, 99, 235, 0.08)",
    border: "1px solid rgba(37, 99, 235, 0.12)",
    color: "#0B5FFF",
    fontWeight: 950,
    letterSpacing: "0.1px",
    fontSize: 13,
    marginBottom: 14,
  },
  h1: {
    margin: 0,
    fontSize: 37,
    lineHeight: 1.11,
    color: "#07142E",
    letterSpacing: "-1.05px",
    maxWidth: 540,
  },
  h2: {
    margin: "14px 0 0",
    fontSize: 15.5,
    lineHeight: 1.55,
    color: "#334155",
    maxWidth: 500,
  },
  ctaRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 18,
  },
  primaryBtn: {
    appearance: "none",
    border: "none",
    cursor: "pointer",
    padding: "12px 16px",
    minHeight: 46,
    borderRadius: 12,
    background: "linear-gradient(135deg, #1E2A78 0%, #2563EB 60%, #0B5FFF 120%)",
    color: "#fff",
    fontWeight: 950,
    fontSize: 14,
    boxShadow: "0 13px 26px rgba(37, 99, 235, 0.24)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  secondaryBtn: {
    appearance: "none",
    border: "1px solid rgba(37,99,235,0.36)",
    cursor: "pointer",
    padding: "11px 15px",
    minHeight: 46,
    borderRadius: 12,
    background: "rgba(255,255,255,0.82)",
    color: "#0B5FFF",
    fontWeight: 950,
    fontSize: 14,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    boxShadow: "0 10px 22px rgba(2,6,23,0.05)",
  },
  inlineIcon: {
    fontSize: 17,
    strokeWidth: 2.4,
  },
  benefitLine: {
    marginTop: 16,
    display: "flex",
    alignItems: "center",
    gap: 9,
    color: "#334155",
    fontWeight: 750,
    fontSize: 13,
    lineHeight: 1.4,
    maxWidth: 500,
  },
  benefitIcon: {
    flex: "0 0 auto",
    color: "#1E2A78",
    fontSize: 18,
    strokeWidth: 2.2,
  },
  heroMetaRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 18,
  },
  metaPill: {
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(255, 255, 255, 0.74)",
    border: "1px solid rgba(15, 23, 42, 0.10)",
    color: "#334155",
    fontWeight: 850,
    fontSize: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    boxShadow: "0 8px 18px rgba(2,6,23,0.04)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  },

  gridSection: {
    marginTop: 8,
    padding: "14px 0 0",
    background: "#F5F7FA",
  },
  gridHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    marginBottom: 18,
    flexDirection: "column",
    textAlign: "center",
  },
  sectionEyebrow: {
    fontSize: 12,
    color: "#0B5FFF",
    textTransform: "uppercase",
    letterSpacing: "0.9px",
    fontWeight: 950,
  },
  gridTitle: {
    margin: 0,
    fontSize: 26,
    color: "#07142E",
    letterSpacing: "-0.45px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },
  card: {
    borderRadius: 18,
    background: "rgba(255, 255, 255, 0.94)",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    overflow: "hidden",
    transition: "transform 0.14s ease, box-shadow 0.14s ease, border-color 0.14s ease",
    boxSizing: "border-box",
  },
  cardClickableArea: {
    appearance: "none",
    border: "none",
    background: "transparent",
    width: "100%",
    display: "flex",
    gap: 13,
    padding: "16px 16px 0",
    alignItems: "center",
    textAlign: "left",
    cursor: "pointer",
    boxSizing: "border-box",
  },
  cardIcon: {
    width: 48,
    height: 48,
    borderRadius: 17,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(15, 23, 42, 0.06)",
    fontSize: 23,
    boxShadow: "0 10px 22px rgba(2, 6, 23, 0.06)",
    flex: "0 0 auto",
  },
  cardTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 950,
    color: "#0F172A",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  badge: {
    fontSize: 10.5,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    background: "rgba(30, 42, 120, 0.08)",
    color: "#1E2A78",
    border: "1px solid rgba(30, 42, 120, 0.12)",
  },
  cardSubtitle: {
    marginTop: 5,
    fontSize: 13,
    lineHeight: 1.4,
    color: "#475569",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardBody: {
    padding: "0 16px 15px",
  },
  accordion: {
    overflow: "hidden",
    transition: "max-height 0.18s ease, opacity 0.18s ease, margin-top 0.18s ease",
  },
  bullets: {
    margin: 0,
    paddingLeft: 18,
    color: "#334155",
    fontSize: 12.8,
    lineHeight: 1.55,
  },
  bulletItem: {
    marginBottom: 5,
  },
  cardBottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginTop: 15,
  },
  openBtn: {
    appearance: "none",
    cursor: "pointer",
    padding: "8px 18px",
    minWidth: 74,
    borderRadius: 11,
    background: "rgba(255,255,255,0.94)",
    color: "#0F172A",
    fontWeight: 900,
    fontSize: 12.5,
    border: "1px solid rgba(15, 23, 42, 0.14)",
    boxShadow: "0 9px 18px rgba(2, 6, 23, 0.05)",
  },
  readMoreBtn: {
    appearance: "none",
    border: "none",
    background: "transparent",
    color: "#475569",
    fontWeight: 850,
    fontSize: 12.5,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "8px 0 8px 8px",
  },

  trustStrip: {
    marginTop: 18,
    padding: "13px 14px",
    borderRadius: 16,
    background: "rgba(255,255,255,0.86)",
    border: "1px solid rgba(15,23,42,0.08)",
    display: "grid",
    gridTemplateColumns: "1.3fr 1fr 1fr 1fr",
    gap: 8,
    alignItems: "center",
    boxShadow: "0 10px 24px rgba(2,6,23,0.045)",
  },
  trustItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 9,
    color: "#334155",
    fontWeight: 820,
    fontSize: 12.5,
    textAlign: "center",
  },

  footer: {
    marginTop: 20,
    paddingTop: 14,
    borderTop: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#F5F7FA",
  },
  footerInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  footerBrand: {
    fontWeight: 950,
    color: "#0F172A",
    letterSpacing: "-0.2px",
  },
  footerLinks: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  footerLinkBtn: {
    appearance: "none",
    border: "1px solid rgba(15, 23, 42, 0.12)",
    background: "rgba(255, 255, 255, 0.86)",
    cursor: "pointer",
    padding: "8px 10px",
    borderRadius: 12,
    color: "#0F172A",
    fontWeight: 850,
    fontSize: 12,
  },
};
